import { apiClient, ApiResponse } from '@/lib/api/client';
import { isFeatureEnabled } from '@/lib/featureFlags';

// Cache for usage data (5 minutes TTL)
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const usageCache = new Map<string, { data: unknown; timestamp: number }>();

// Helper function to get cached data
function getCachedData<T>(key: string): T | null {
  const cached = usageCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T;
  }
  return null;
}

// Helper function to set cached data
function setCachedData(key: string, data: unknown): void {
  usageCache.set(key, { data, timestamp: Date.now() });
}

// Helper function for retry logic
async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry on client errors (4xx)
      if (lastError.message?.includes('40')) {
        throw lastError;
      }
      
      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
      }
    }
  }
  
  throw lastError!;
}

// Types for usage data
export interface UsageMetrics {
  period: string;
  testsRun: number;
  testsLimit: number;
  apiCalls: number;
  apiLimit: number;
  dataTransfer: number; // in MB
  dataTransferLimit: number; // in MB
  storageUsed: number; // in GB
  storageLimit: number; // in GB
  activeUsers: number;
  userLimit: number;
  billingCycle: {
    startDate: string;
    endDate: string;
    daysRemaining: number;
  };
  planName: string;
  currentCharges: number;
  estimatedCharges: number;
}

export interface UsageHistory {
  date: string;
  testsRun: number;
  apiCalls: number;
  dataTransfer: number; // in MB
  activeUsers: number;
}

export interface BillingInfo {
  planName: string;
  billingCycle: {
    startDate: string;
    endDate: string;
    daysRemaining: number;
  };
  currentCharges: number;
  estimatedCharges: number;
  nextPaymentDate: string;
  paymentMethod?: {
    type: string;
    lastFour: string;
    expiryDate: string;
  };
}

export interface UsageExportRequest {
  period: string;
  format: 'csv' | 'json' | 'pdf';
  includeHistory?: boolean;
  includeBilling?: boolean;
}


/**
 * Get usage metrics for a specific time period
 */
export async function getUsageMetrics(params: { 
  period: string;
  organizationId?: string;
}): Promise<ApiResponse<UsageMetrics>> {
  const cacheKey = `usage-metrics-${params.period}-${params.organizationId || 'default'}`;
  
  // Check cache first
  const cachedData = getCachedData<UsageMetrics>(cacheKey);
  if (cachedData) {
    return {
      success: true,
      data: cachedData
    };
  }

  try {
    // Real API call with retry logic
    const result = await retryOperation(async () => {
      const queryParams = new URLSearchParams({
        period: params.period
      });
      
      if (params.organizationId) {
        queryParams.append('organization_id', params.organizationId);
      }
      
      return await apiClient.get<UsageMetrics>(`/api/usage/metrics?${queryParams}`);
    });

    // Cache successful results
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }

    return result;
  } catch (error) {
    console.error('Error fetching usage metrics:', error);
    
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch usage metrics'
    };
  }
}

/**
 * Get usage history for charting
 */
export async function getUsageHistory(params: { 
  period: string;
  organizationId?: string;
}): Promise<ApiResponse<UsageHistory[]>> {
  try {
    // Real API call
    const queryParams = new URLSearchParams({
      period: params.period
    });
    
    if (params.organizationId) {
      queryParams.append('organization_id', params.organizationId);
    }
    
    return await apiClient.get<UsageHistory[]>(`/api/usage/history?${queryParams}`);
  } catch (error) {
    console.error('Error fetching usage history:', error);
    
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch usage history'
    };
  }
}

/**
 * Get billing information
 */
export async function getBillingInfo(params?: { 
  organizationId?: string;
}): Promise<ApiResponse<BillingInfo>> {
  try {
    // Real API call
    const queryParams = new URLSearchParams();
    if (params?.organizationId) {
      queryParams.append('organization_id', params.organizationId);
    }
    
    const url = `/api/billing/info${queryParams.toString() ? `?${queryParams}` : ''}`;
    return await apiClient.get<BillingInfo>(url);
  } catch (error) {
    console.error('Error fetching billing info:', error);
    
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch billing information'
    };
  }
}

/**
 * Export usage data
 */
export async function exportUsageData(request: UsageExportRequest): Promise<ApiResponse<{ download_url: string }>> {
  try {
    // Real API call
    return await apiClient.post<{ download_url: string }>('/api/usage/export', request);
  } catch (error) {
    console.error('Error exporting usage data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to export usage data'
    };
  }
}

/**
 * Get failures over time data for charting
 * Organization is inferred from authenticated user's JWT token for security
 */
export async function getFailuresOverTime(params: { 
  period: string;
}): Promise<ApiResponse<Array<{ date: string; failures: number; dateObj?: Date }>>> {
  const cacheKey = `failures-over-time-${params.period}`;
  
  // Check cache first
  const cachedData = getCachedData<Array<{ date: string; failures: number; dateObj?: Date }>>(cacheKey);
  if (cachedData) {
    return {
      success: true,
      data: cachedData
    };
  }

  try {
    // Real API call with retry logic
    const result = await retryOperation(async () => {
      const queryParams = new URLSearchParams({
        period: params.period
      });
      
      return await apiClient.get<{
        period: string;
        interval_hours: number;
        failures_over_time: Array<{
          date: string;
          failures: number;
          total_runs: number;
          failure_rate: number;
        }>;
        summary: {
          total_failures: number;
          total_runs: number;
          average_failure_rate: number;
        };
      }>(`/api/metrics/failures-over-time?${queryParams}`);
    });

    // Transform API response to match expected format
    if (result.success && result.data) {
      const transformedData = result.data.daily_metrics.map((point) => ({
        date: point.date,
        failures: point.failed_tests,
        total_runs: point.total_tests,
        failure_rate: point.failure_rate_percentage,
        dateObj: new Date(point.date)
      }));
      
      // Cache successful results
      setCachedData(cacheKey, transformedData);
      
      return {
        success: true,
        data: transformedData
      };
    }

    return result;
  } catch (error) {
    console.error('Error fetching failures over time data:', error);
    
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch failures over time data'
    };
  }
}

/**
 * Get current usage summary for dashboard meter
 */
export async function getCurrentUsageSummary(params?: { 
  organizationId?: string;
}): Promise<ApiResponse<{
  currentMonthRuns: number;
  planLimit: number;
  planName: string;
  usagePercentage: number;
  daysRemaining: number;
}>> {
  const cacheKey = `usage-summary-${params?.organizationId || 'default'}`;
  
  // Check cache first (shorter TTL for summary)
  const cachedData = getCachedData<{
    currentMonthRuns: number;
    planLimit: number;
    planName: string;
    usagePercentage: number;
    daysRemaining: number;
  }>(cacheKey);
  
  if (cachedData) {
    return {
      success: true,
      data: cachedData
    };
  }

  try {
    // Real API call with retry logic
    const result = await retryOperation(async () => {
      const queryParams = new URLSearchParams();
      if (params?.organizationId) {
        queryParams.append('organization_id', params.organizationId);
      }
      
      const url = `/api/usage/current${queryParams.toString() ? `?${queryParams}` : ''}`;
      return await apiClient.get<{
        currentMonthRuns: number;
        planLimit: number;
        planName: string;
        usagePercentage: number;
        daysRemaining: number;
      }>(url);
    });

    // Cache successful results
    if (result.success && result.data) {
      setCachedData(cacheKey, result.data);
    }

    return result;
  } catch (error) {
    console.error('Error fetching current usage summary:', error);
    
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch current usage summary'
    };
  }
}

// Type definition for detailed usage statistics
export interface DetailedUsageStats {
  time_range: string;
  summary: {
    total_api_calls: number;
    total_runs: number;
    total_compute_hours: number;
    total_cost: number;
    unique_users: number;
    unique_projects: number;
  };
  daily_breakdown: Array<{
    date: string;
    api_calls: number;
    runs: number;
    compute_hours: number;
    cost: number;
    active_users: number;
  }>;
  top_users: Array<{
    user_id: string;
    user_name: string;
    api_calls: number;
    runs: number;
    compute_hours: number;
    cost: number;
  }>;
  top_projects: Array<{
    project_id: string;
    project_name: string;
    api_calls: number;
    runs: number;
    cost: number;
  }>;
}

/**
 * Get detailed usage statistics for MCP tools
 * Supports the evalops_get_usage_stats MCP tool
 */
export async function getDetailedUsageStats(
  organizationId: string,
  timeRange: "7d" | "30d" | "90d" | "1y" = "30d"
): Promise<ApiResponse<DetailedUsageStats>> {
  const cacheKey = `usage-stats-${organizationId}-${timeRange}`;
  
  // Check cache first
  const cached = getCachedData<DetailedUsageStats>(cacheKey);
  if (cached && isFeatureEnabled("cacheUsageData")) {
    return { success: true, data: cached };
  }

  try {
    const response = await retryOperation(async () => {
      return await apiClient.get<DetailedUsageStats>(`/organizations/${organizationId}/usage/detailed?time_range=${timeRange}`);
    });

    // Cache successful response
    if (response.success && isFeatureEnabled("cacheUsageData")) {
      setCachedData(cacheKey, response.data);
    }

    return response;
  } catch (error) {
    console.error("Failed to get detailed usage stats:", error);
    throw error;
  }
}
